import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Briefcase, Check, ChevronRight, Plus, Search, X } from 'lucide-react';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import {
  describePortalError,
  getPortalPhaseAccountCodes,
  getPortalProjectPhases,
  getPortalRecentProjects,
  searchPortalProjects,
} from '../../../api/portal';
import { isOverhead, jobTitle, OVERHEAD_TITLE, sameJob } from './portalModel';
import type {
  PortalAccountCode,
  PortalJob,
  PortalPhase,
  PortalProjectSearchResult,
  PortalRecentProject,
} from './portalTypes';

/**
 * "What are you working on?" (handoff §2, screenshots 10–13): Project → Phase → Cost code,
 * every step searchable, each breadcrumb step clickable to go back. Office / Overhead
 * applies at once; a Recent combination (GET /portal/projects/recent) applies at once too;
 * any other project goes through its phases and cost codes (/portal/projects/*).
 * The body is a fixed 400px scroll area (D7), so loading, empty and error states never
 * resize the dialog.
 */

type Step = 'project' | 'phase' | 'cc';

interface Props {
  currentJob: PortalJob;
  overhead: PortalJob;
  employeeID: number;
  /** Sub-line: "Switching closes your current segment at 3:02 PM…" or "Applies when you clock in". */
  subLine: string;
  onPick: (job: PortalJob) => void;
  onClose: () => void;
  /** A 401 while browsing. */
  onSessionExpired: () => void;
  /** Dialog title (default "What are you working on?"). */
  title?: string;
  /** The non-job choice's name / sub-line (default "Office / Overhead" · "Default for office time"). */
  overheadLabel?: string;
  overheadSub?: string;
}

const MIN_SEARCH = 2;

function matches(q: string, ...fields: (string | null | undefined)[]): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  return fields.some((f) => (f ?? '').toLowerCase().includes(needle));
}

function recentToJob(r: PortalRecentProject): PortalJob {
  return {
    projectID: r.projectID,
    projectPhaseID: r.projectPhaseID,
    accountCodeID: r.accountCodeID,
    projectCode: r.altProjectID,
    projectName: r.projectName,
    phaseCode: r.altProjectPhaseID,
    phaseName: r.projectPhaseName,
    costCode: r.altAccountCodeID,
    costName: r.accountCodeName,
  };
}

const JobPicker: React.FC<Props> = ({
  currentJob, overhead, employeeID, subLine, onPick, onClose, onSessionExpired,
  title = 'What are you working on?', overheadLabel = OVERHEAD_TITLE, overheadSub = 'Default for office time',
}) => {
  const [step, setStep] = useState<Step>('project');
  const [project, setProject] = useState<PortalProjectSearchResult | null>(null);
  const [phase, setPhase] = useState<PortalPhase | null>(null);
  const [q, setQ] = useState('');
  const settledQ = useDebouncedValue(q.trim(), 300);

  const [recent, setRecent] = useState<PortalRecentProject[] | null>(null);
  const [results, setResults] = useState<PortalProjectSearchResult[] | null>(null);
  const [phases, setPhases] = useState<PortalPhase[] | null>(null);
  const [codes, setCodes] = useState<PortalAccountCode[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const fail = (err: unknown) => {
    const info = describePortalError(err);
    if (info.kind === 'sessionExpired') onSessionExpired();
    setError(info.message);
  };

  useEffect(() => {
    getPortalRecentProjects(10).then(setRecent).catch((e) => { setRecent([]); fail(e); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { inputRef.current?.focus(); }, [step]);

  // Project search (server-side, debounced).
  useEffect(() => {
    if (step !== 'project' || settledQ.length < MIN_SEARCH) {
      setResults(null);
      return;
    }
    let live = true;
    setResults(null);
    searchPortalProjects(settledQ, employeeID)
      .then((r) => { if (live) setResults(r); })
      .catch((e) => { if (live) { setResults([]); fail(e); } });
    return () => { live = false; };
  }, [settledQ, step, employeeID]); // eslint-disable-line react-hooks/exhaustive-deps

  const goStep = (next: Step) => {
    setStep(next);
    setQ('');
    setError(null);
    if (next === 'project') { setProject(null); setPhase(null); setPhases(null); setCodes(null); }
    if (next === 'phase') { setPhase(null); setCodes(null); }
  };

  const pickProject = (p: PortalProjectSearchResult) => {
    setProject(p);
    setPhase(null);
    setPhases(null);
    setCodes(null);
    setStep('phase');
    setQ('');
    setError(null);
    getPortalProjectPhases(p.projectID).then(setPhases).catch((e) => { setPhases([]); fail(e); });
  };

  const pickPhase = (ph: PortalPhase) => {
    if (!project) return;
    setPhase(ph);
    setCodes(null);
    setStep('cc');
    setQ('');
    setError(null);
    getPortalPhaseAccountCodes(project.projectID, ph.projectPhaseID).then(setCodes).catch((e) => { setCodes([]); fail(e); });
  };

  const pickCode = (c: PortalAccountCode) => {
    if (!project || !phase) return;
    onPick({
      projectID: project.projectID,
      projectPhaseID: phase.projectPhaseID,
      accountCodeID: c.accountCodeID,
      projectCode: project.altProjectID,
      projectName: project.name,
      phaseCode: phase.altProjectPhaseID,
      phaseName: phase.name,
      costCode: c.altAccountCodeID,
      costName: c.name,
    });
  };

  const recentJobs = useMemo(
    () => (recent ?? []).filter((r) => r.projectID != null).map(recentToJob),
    [recent],
  );

  const placeholder = step === 'project' ? 'Search projects by name or number' : step === 'phase' ? 'Search phases' : 'Search cost codes';
  const qq = q.trim();
  const none = <div className="pk-empty">Nothing matches “{qq}”</div>;
  const loadingRow = <div className="pk-empty">Loading…</div>;

  const item = (opts: {
    key: string; cd: string; nm: string; sub?: string; on: boolean; onClick: () => void; cls?: string; apply?: boolean;
  }) => (
    <button type="button" key={opts.key} className={`pk-item ${opts.cls ?? ''}${opts.on ? ' on' : ''}`} onClick={opts.onClick}>
      <span className="cd">{opts.cd}</span>
      <span className="nm">{opts.nm}{opts.sub ? <small>{opts.sub}</small> : null}</span>
      {opts.apply ? (opts.on ? <Check /> : <Plus />) : <ChevronRight />}
    </button>
  );

  let body: React.ReactNode;
  if (step === 'project') {
    const showOverhead = matches(qq, 'OVH', overheadLabel, OVERHEAD_TITLE, 'overhead', 'non-job');
    const rec = recentJobs.filter((j) => matches(qq, j.projectCode, j.projectName, j.phaseName, j.costCode, j.costName));
    body = (
      <>
        {showOverhead && (
          <>
            <div className="pk-sec">Not job related</div>
            <div className="pk-list">
              {item({ key: 'ovh', cd: 'OVH', nm: overheadLabel, sub: overheadSub, on: isOverhead(currentJob), onClick: () => onPick(overhead), cls: 'ovh', apply: true })}
            </div>
          </>
        )}
        <div className="pk-sec">Recent</div>
        {recent == null ? loadingRow : rec.length ? (
          <div className="pk-list">
            {rec.map((j) => item({
              key: `r-${j.projectID}-${j.projectPhaseID}-${j.accountCodeID}`,
              cd: j.projectCode ?? '',
              nm: jobTitle(j),
              sub: [[j.phaseCode, j.phaseName].filter(Boolean).join(' '), [j.costCode, j.costName].filter(Boolean).join(' ')].filter(Boolean).join(' · '),
              on: sameJob(j, currentJob),
              onClick: () => onPick(j),
              apply: true,
            }))}
          </div>
        ) : <div className="pk-note">{qq ? `No recent jobs match “${qq}”` : 'No jobs in the last 30 days'}</div>}
        <div className="pk-sec">{qq ? 'Matching projects' : 'All projects'}</div>
        {qq.length < MIN_SEARCH ? (
          <div className="pk-note">Type at least {MIN_SEARCH} characters to search every project</div>
        ) : results == null ? loadingRow : results.length ? (
          <div className="pk-list">
            {results.map((p) => item({
              key: `p-${p.projectID}`, cd: p.altProjectID, nm: p.name, on: currentJob.projectID === p.projectID, onClick: () => pickProject(p),
            }))}
          </div>
        ) : none}
      </>
    );
  } else if (step === 'phase') {
    const list = (phases ?? []).filter((ph) => matches(qq, ph.altProjectPhaseID, ph.name));
    body = (
      <>
        <div className="pk-sec">{project?.altProjectID} · {project?.name}</div>
        {phases == null ? loadingRow : list.length ? (
          <div className="pk-list">
            {list.map((ph) => item({
              key: `ph-${ph.projectPhaseID}`, cd: ph.altProjectPhaseID, nm: ph.name, sub: ph.description ?? undefined,
              on: currentJob.projectPhaseID === ph.projectPhaseID, onClick: () => pickPhase(ph),
            }))}
          </div>
        ) : qq ? none : <div className="pk-empty">This project has no active phases</div>}
      </>
    );
  } else {
    const list = (codes ?? []).filter((c) => matches(qq, c.altAccountCodeID, c.name, c.description));
    body = (
      <>
        <div className="pk-sec">{project?.altProjectID} · Phase {phase?.altProjectPhaseID} {phase?.name}</div>
        {codes == null ? loadingRow : list.length ? (
          <div className="pk-list">
            {list.map((c) => item({
              key: `cc-${c.accountCodeID}`, cd: c.altAccountCodeID, nm: c.name, sub: c.description ?? undefined,
              on: currentJob.projectPhaseID === phase?.projectPhaseID && currentJob.accountCodeID === c.accountCodeID,
              onClick: () => pickCode(c), apply: true,
            }))}
          </div>
        ) : qq ? none : <div className="pk-empty">This phase has no cost codes</div>}
      </>
    );
  }

  return (
    <div className="etp-modal-bg" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="etp-modal wide picker" role="dialog" aria-modal="true" aria-labelledby="etp-picker-title">
        <div className="modal-head">
          <div className="mi"><Briefcase /></div>
          <div>
            <h3 id="etp-picker-title">{title}</h3>
            <div className="ms">{subLine}</div>
          </div>
          <button type="button" className="x" onClick={onClose} aria-label="Close"><X /></button>
        </div>
        <div className="pk-top">
          <div className="pk-crumbs">
            {step === 'project' ? <span className="cur">Project</span> : (
              <button type="button" onClick={() => goStep('project')} title={project?.name}>{project?.altProjectID}</button>
            )}
            {step !== 'project' && (
              <>
                <ChevronRight />
                {step === 'phase' ? <span className="cur">Phase</span> : (
                  <button type="button" onClick={() => goStep('phase')}>Phase {phase?.altProjectPhaseID}</button>
                )}
              </>
            )}
            {step === 'cc' && (<><ChevronRight /><span className="cur">Cost code</span></>)}
          </div>
          <div className="searchwrap">
            <Search />
            <input ref={inputRef} type="text" placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>
        <div className="modal-body">{body}</div>
        <div className="modal-foot">
          <span className="grow mfoot-note" style={{ color: error ? 'var(--danger)' : undefined }}>
            {error ?? <>Now: <b>{jobTitle(currentJob)}</b></>}
          </span>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default JobPicker;
